// @ts-nocheck
import { useForm, useStore } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { useState, useRef, useMemo, useEffect } from "react";
import {
  quotationFormSchema,
  quotationDetailSchema,
  type QuotationFormData,
  type QuotationDetailFormData,
} from "../../../shared/schemas/quotation.schema";
import { Button } from "@/components/ui/button";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, formatMoney, formatNumber, validateExcelFile, extractErrorMessage } from "@/lib/utils";
import { resolveProductCatalogPricing } from "@/shared/helpers/product-costing.helper";
import { focusFirstError, hasFieldError, collectFormErrorMessages } from "../../../lib/form/formUtils";
import {
  Save,
  Loader2,
  FileText,
  Info,
  Plus,
  ExternalLink,
  FileDown,
  Upload,
  AlertTriangle,
  Copy,
  Edit,
  UserCog,
  Truck,
  Layers,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CustomerFormModal from "../../master/customers/CustomerFormModal";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { QuotationEstimatingModal } from "./QuotationEstimatingModal";
import { QuotationDetailModal } from "./QuotationDetailModal";
import ProductFormModal from "../../master/products/ProductFormModal";
import ProductEditModal from "../../master/products/ProductEditModal";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import InlineDetailTable, {
  type DetailColumn,
} from "@/components/shared/InlineDetailTable";
import {
  downloadExcelTemplate,
  parseExcelFile,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { EditableCombobox } from "@/components/shared/EditableCombobox";
import { NumericInput } from "@/components/shared/NumericInput";
import { useUserLookup } from "@/hooks/useFKLookup";
import { useConfigSuggestions } from "@/hooks/useConfigSuggestions";
import { useAuthStore } from "@/stores/auth.store";
import { useRoleAccess } from "@/hooks/useRoleAccess";

import { trpcClient } from "@/lib/trpc";
import { productsApi } from "@/lib/api-client";

import CurrencyExchangeField from "@/components/shared/CurrencyExchangeField";
import { formatProductSpecs } from "@/lib/format-specs";

import { toVnDateString } from "@apv-erp/status";
interface QuotationFormProps {
  defaultValues?: Partial<QuotationFormData>;
  orderCount?: number;
  onSubmit: (data: QuotationFormData) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

/* ── Helpers ── */

const formatSpecs = formatProductSpecs;

const EMPTY_DETAIL: QuotationDetailFormData = {
  productId: "",
  productCode: "",
  productName: "",
  specs: "",
  process: "",
  unit: "Cái",
  quantity: 1,
  priceVND: 0,
  priceUSD: 0,
  costPrice: 0,
  profitMargin: 0,
  quotedPrice: 0,
  notes: "",
  isCombo: false,
  isCommercial: false,
};

// Columns definition moved inside component to access product lookup

const DETAIL_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "productCode", label: "Mã SP" },
  { key: "productName", label: "Tên SP", required: true },
  { key: "specs", label: "Quy cách" },
  { key: "unit", label: "Đơn vị" },
  { key: "quantity", label: "Số lượng", type: "int", required: true },
  { key: "priceVND", label: "Giá VND", type: "decimal", required: true },
  { key: "priceUSD", label: "Giá USD", type: "decimal" },
  { key: "costPrice", label: "Giá vốn", type: "decimal" },
  { key: "profitMargin", label: "Margin %", type: "decimal" },
  { key: "notes", label: "Ghi chú" },
];

const QUOTATION_SAMPLE_DATA = [
  {
    productCode: "VNF-091644",
    productName: "Hộp COLOR BOX",
    specs: "Duplex450gsm W175*D175*H160mm 4C+Cán màng bóng+Bế+Dán cạnh",
    unit: "PCS",
    quantity: 20000,
    priceVND: 10700,
    priceUSD: 0.407,
    costPrice: 8500,
    profitMargin: 20,
    notes: "1PCS",
  },
  {
    productCode: "VNF-091692",
    productName: "Hộp POSTER",
    specs: "Art250gsm 594x841mm 4C+Cán màng mờ",
    unit: "PCS",
    quantity: 5000,
    priceVND: 3200,
    priceUSD: 0.122,
    costPrice: 2400,
    profitMargin: 25,
    notes: "",
  },
];

export default function QuotationForm({
  defaultValues,
  orderCount,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
}: QuotationFormProps) {
  const currentUser = useAuthStore((s) => s.user);
  const [estimatingRowId, setEstimatingRowId] = useState<string | null>(null);
  const [estimatingRowIndex, setEstimatingRowIndex] = useState<number | null>(
    null,
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editDetailIndex, setEditDetailIndex] = useState<number | null>(null);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);

  // Lưu nháp / Lưu (plan-draft-and-save-buttons-v1): báo giá đang nháp bấm "Lưu" là chuyển bộ phận tính giá,
  // nên chưa bắt buộc giá báo; báo giá đã phát hành vẫn bắt buộc đủ giá như cũ
  const currentStatus = (defaultValues as any)?.status as string | undefined;
  const isPublishingDraft = !currentStatus || currentStatus === "DRAFT";
  const saveModeRef = useRef<"DRAFT" | "SUBMIT">("SUBMIT");
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  const form = useForm({
    defaultValues: {
      quotationId: defaultValues?.quotationId ?? "",
      customerId: defaultValues?.customerId ?? "",
      customerName: defaultValues?.customerName ?? "",
      details: defaultValues?.details ?? [],
      createdDate:
        defaultValues?.createdDate ?? toVnDateString(),
      validUntil:
        defaultValues?.validUntil ??
        toVnDateString(Date.now() + 30 * 24 * 60 * 60 * 1000),
      exchangeRate: defaultValues?.exchangeRate ?? undefined,
      currency: defaultValues?.currency ?? ("VND" as const),
      shippingFee: defaultValues?.shippingFee ?? 0,
      vatRate: defaultValues?.vatRate ?? 0,
      deposit: defaultValues?.deposit ?? "",
      paymentTerm: defaultValues?.paymentTerm ?? "",
      margin: defaultValues?.margin ?? undefined,
      deliveryAddress: defaultValues?.deliveryAddress ?? "",
      contactPerson: defaultValues?.contactPerson ?? "",
      phone: defaultValues?.phone ?? "",
      email: defaultValues?.email ?? "",
      deliveryTime: defaultValues?.deliveryTime ?? "",
      status: defaultValues?.status ?? ("DRAFT" as const),
      progress: defaultValues?.progress ?? "",
      notes: defaultValues?.notes ?? "",
      quantityTolerance: defaultValues?.quantityTolerance ?? "",
      isNonMoq: defaultValues?.isNonMoq ?? false,
      isOutsourced: defaultValues?.isOutsourced ?? false,
      supplierCost: defaultValues?.supplierCost ?? undefined,
      creatorId: defaultValues?.creatorId ?? currentUser?.id ?? "",
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: quotationFormSchema as any },
    onSubmit: async ({ value }) => {
      // Báo giá đã phát hành: bắt buộc mọi dòng sản phẩm có quotedPrice > 0
      for (let i = 0; !isPublishingDraft && i < value.details.length; i++) {
        const item = value.details[i];
        if (!item.quotedPrice || Number(item.quotedPrice) <= 0) {
          toast.error(`Sản phẩm "${item.productName || 'Dòng ' + (i + 1)}" chưa nhập Giá báo chốt. Vui lòng nhập giá báo > 0đ để lưu báo giá!`);
          return;
        }
      }
      return onSubmit({ ...(value as QuotationFormData), saveMode: saveModeRef.current } as any);
    },
  });

  // Note: isOutsourced removed from UI, keeping variable for safety if needed
  const isOutsourcedValue = false;

  // Reactive sync for creatorId when currentUser is loaded asynchronously
  useEffect(() => {
    if (!form.getFieldValue("creatorId") && currentUser?.id) {
      form.setFieldValue("creatorId", currentUser.id);
    }
  }, [currentUser?.id, form]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    saveModeRef.current = "SUBMIT";
    // Gom TẤT CẢ dòng thiếu Giá báo chốt (thay vì dừng ở dòng đầu tiên) — chỉ với báo giá đã phát hành
    const details = form.getFieldValue("details") || [];
    const priceMessages: string[] = [];
    let firstMissingIndex = -1;
    for (let i = 0; !isPublishingDraft && i < details.length; i++) {
      const item = details[i];
      if (!item.quotedPrice || Number(item.quotedPrice) <= 0) {
        if (firstMissingIndex < 0) firstMissingIndex = i;
        priceMessages.push(
          `Sản phẩm "${item.productName || "Dòng " + (i + 1)}" chưa nhập Giá báo chốt (cần lớn hơn 0đ)`,
        );
      }
    }

    if (priceMessages.length > 0) {
      // Chạy validate toàn form để lấy luôn các lỗi khác và liệt kê chung một lần
      await form.validateAllFields("submit");
      const otherMessages = collectFormErrorMessages(form.state as any).filter(
        (m) => !priceMessages.includes(m),
      );
      const all = [...priceMessages, ...otherMessages];
      toast.error(
        `Còn ${all.length} mục chưa hợp lệ:\n` +
          all.slice(0, 8).map((m) => `• ${m}`).join("\n") +
          (all.length > 8 ? `\n... và ${all.length - 8} mục khác` : ""),
        { duration: 9000 },
      );
      const el = document.querySelector(
        `input[data-field="details.${firstMissingIndex}.quotedPrice"]`,
      ) as HTMLElement;
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    form.handleSubmit();

    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  // Lưu nháp: ghi tạm thông tin đang có (chỉ cần khách hàng), không kiểm tra đủ thông tin
  const handleSaveDraft = async () => {
    const values = form.state.values as any;
    if (!values.customerId) {
      toast.error("Chọn khách hàng trước khi lưu nháp");
      return;
    }
    saveModeRef.current = "DRAFT";
    setIsDraftSaving(true);
    try {
      await onSubmit({ ...values, saveMode: "DRAFT" } as any);
    } finally {
      setIsDraftSaving(false);
      saveModeRef.current = "SUBMIT";
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const handleImportDetails = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateExcelFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setImporting(true);
    try {
      const result = await parseExcelFile(file);
      if (result.rows.length === 0) {
        toast.error("File không có dữ liệu");
        return;
      }
      const newDetails: QuotationDetailFormData[] = result.rows.map((row) => ({
        productCode: row["Mã SP"] || row["productCode"] || "",
        productName: row["Tên SP"] || row["productName"] || "",
        specs: row["Quy cách"] || row["specs"] || "",
        unit: row["Đơn vị"] || row["unit"] || "tờ",
        quantity: parseInt(row["Số lượng"] || row["quantity"] || "0", 10),
        priceVND: parseFloat(
          (row["Giá VND"] || row["priceVND"] || "0").replace(/,/g, ""),
        ),
        priceUSD:
          parseFloat(
            (row["Giá USD"] || row["priceUSD"] || "0").replace(/,/g, ""),
          ) || 0,
        costPrice:
          parseFloat(
            (row["Giá vốn"] || row["costPrice"] || "0").replace(/,/g, ""),
          ) || 0,
        profitMargin:
          parseFloat(
            (row["Margin %"] || row["profitMargin"] || "0").replace(/,/g, ""),
          ) || 0,
        notes: row["Ghi chú"] || row["notes"] || "",
      }));

      const currentDetails =
        (form.getFieldValue("details") as QuotationDetailFormData[]) || [];
      const nonEmpty = currentDetails.filter(
        (d) => d.productName || d.productCode,
      );
      form.setFieldValue("details", [
        ...nonEmpty,
        ...newDetails,
      ] as QuotationDetailFormData[]);
    } catch (err: any) {
      toast.error(`Lỗi import: ${extractErrorMessage(err)}`);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const userLookup = useUserLookup();
  const { hasPermission } = useRoleAccess();
  const canAddMasterData =
    hasPermission("products", "create") || hasPermission("customers", "create");

  // Track selected customerId for product filtering
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    defaultValues?.customerId || "",
  );

  const queryClient = useQueryClient();
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloningInitialData, setCloningInitialData] = useState<any>(null);
  const [cloningRowIndex, setCloningRowIndex] = useState<number | null>(null);
  const [isPreparingClone, setIsPreparingClone] = useState(false);

  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingProductRowIndex, setEditingProductRowIndex] = useState<number | null>(null);

  const handleEditProductSuccess = (updated: any) => {
    if (updated && editingProductRowIndex !== null) {
      const currentRows = (form.getFieldValue("details") as QuotationDetailFormData[]) || [];
      if (currentRows[editingProductRowIndex]) {
        const updatedRows = [...currentRows];
        const specsStr = (typeof updated.specs === "object" && updated.specs ? formatProductSpecs(updated.specs, updated) : updated.specs) || updated.packingSpec || updatedRows[editingProductRowIndex].specs;
        const resolved = resolveProductCatalogPricing(updated);
        const row = updatedRows[editingProductRowIndex];
        const newCost = resolved.costPrice > 0 ? resolved.costPrice : (row.costPrice || 0);
        // Giá bán trên dòng do kinh doanh nhập: sửa sản phẩm không ghi đè. Chỉ điền đơn giá niêm yết khi dòng chưa có giá.
        const rowPrice = Number(row.quotedPrice || row.priceVND || 0);
        const fillPrice = rowPrice > 0 ? null : (resolved.catalogPrice > 0 ? resolved.catalogPrice : null);
        updatedRows[editingProductRowIndex] = {
          ...row,
          productCode: updated.productCode || updated.code || row.productCode,
          productName: updated.productName || updated.name || row.productName,
          unit: updated.unit || row.unit || "Cái",
          specs: specsStr,
          costPrice: newCost,
          ...(fillPrice ? { priceVND: fillPrice, quotedPrice: fillPrice } : {}),
        };
        form.setFieldValue("details", updatedRows);
        toast.success(`Đã cập nhật thông số sản phẩm [${updated.productCode || updated.code}] vào dòng báo giá!`);
      }
    }
    setEditingProductId(null);
    setEditingProductRowIndex(null);
  };

  const handleStartClone = async (row: QuotationDetailFormData, index: number) => {
    if (!row.productId) return;
    setIsPreparingClone(true);
    setCloningRowIndex(index);
    try {
      const prod: any = await trpcClient.products.getById.query({ id: row.productId });

      let newCode = "";
      const custId = selectedCustomerId || prod?.customerId;
      if (custId) {
        try {
          const gen = await trpcClient.products.generateCode.query({ customerId: custId });
          if (gen?.code) newCode = gen.code;
        } catch {
          // ignore
        }
      }
      if (!newCode) {
        newCode = `SP-${Date.now().toString().slice(-5)}`;
      }

      let parsedSpecs: any = {};
      try {
        if (typeof prod.specs === "object" && prod.specs) parsedSpecs = prod.specs;
        else if (typeof prod.specs === "string" && prod.specs.startsWith("{")) {
          parsedSpecs = JSON.parse(prod.specs);
        }
      } catch {}

      // Sanitize null -> undefined/'' cho tất cả optional string fields từ DB
      // DB trả null cho optional fields, nhưng Zod schema chỉ chấp nhận string | undefined
      const sanitizeNulls = <T extends Record<string, unknown>>(obj: T): T => {
        const result = { ...obj };
        for (const key of Object.keys(result)) {
          if (result[key] === null) {
            result[key] = undefined as any;
          }
        }
        return result;
      };

      const rawMaterials = prod.productMaterials || prod.materials || [];
      const rawProcesses = prod.productProcesses || prod.processes || prod.processConfigs || [];

      const initialData: any = {
        productCode: newCode,
        productName: `${prod.productName || prod.name || row.productName || ""} (Bản sao)`,
        customerId: custId || undefined,
        customerName: form.getFieldValue("customerName") || prod.customer?.name || "",
        typeId: prod.typeId || prod.productType?.id || undefined,
        unit: prod.unit || row.unit || "hộp",
        length: prod.length ? Number(prod.length) : undefined,
        width: prod.width ? Number(prod.width) : undefined,
        height: prod.height ? Number(prod.height) : undefined,
        sheetLength: prod.sheetLength ? Number(prod.sheetLength) : undefined,
        sheetWidth: prod.sheetWidth ? Number(prod.sheetWidth) : undefined,
        costPrice: prod.costPrice ? Number(prod.costPrice) : undefined,
        price: prod.price ? Number(prod.price) : undefined,
        moldId: prod.moldId || (prod.dieCutProducts?.[0]?.dieCutId) || undefined,
        specs: parsedSpecs,
        materialIds: prod.productMaterials?.map((m: any) => m.materialId) || prod.materials?.map((m: any) => m.materialId) || prod.materialIds || [],
        processIds: prod.productProcesses?.map((p: any) => p.processId) || prod.processes?.map((p: any) => p.processId) || prod.processIds || [],
        processConfigs: rawProcesses.map((p: any) => sanitizeNulls(p)),
        productMaterials: rawMaterials.map((m: any) => sanitizeNulls(m)),
        notes: prod.notes || row.notes || "",
      };

      setCloningInitialData(initialData);
      setCloneModalOpen(true);
    } catch (err: any) {
      toast.error(err?.message || "Không thể tải thông tin sản phẩm để sao chép");
    } finally {
      setIsPreparingClone(false);
    }
  };

  const handleCloneProductSuccess = (newItem: any) => {
    const newProduct = newItem?.original || newItem;
    const currentRows = (form.getFieldValue("details") as QuotationDetailFormData[]) || [];
    const sourceRow = cloningRowIndex !== null ? currentRows[cloningRowIndex] : null;

    const specsStr = formatSpecs(newProduct.specs, newProduct) || newProduct.packingSpec || sourceRow?.specs || "";
    const processStr = newProduct.process || sourceRow?.process || "";
    const resolvedClonePricing = resolveProductCatalogPricing(newProduct);
    const catalogPrice = resolvedClonePricing.catalogPrice;

    const newRow: QuotationDetailFormData = {
      productId: newProduct.id,
      productCode: newProduct.productCode || newProduct.code || "",
      productName: newProduct.productName || newProduct.name || "",
      specs: specsStr,
      process: processStr,
      unit: newProduct.unit || sourceRow?.unit || "tờ",
      quantity: sourceRow?.quantity || 1, // Kế thừa số lượng dòng gốc
      priceVND: sourceRow?.priceVND ?? catalogPrice,
      priceUSD: sourceRow?.priceUSD ?? 0,
      costPrice: sourceRow?.costPrice ?? resolvedClonePricing.costPrice,
      profitMargin: sourceRow?.profitMargin ?? resolvedClonePricing.profitMargin,
      quotedPrice: sourceRow?.quotedPrice ?? sourceRow?.priceVND ?? catalogPrice,
      notes: sourceRow?.notes || "",
      isCombo: sourceRow?.isCombo || false,
      isCommercial: sourceRow?.isCommercial || false,
    };

    const updatedRows = [...currentRows];
    const insertIndex = cloningRowIndex !== null ? cloningRowIndex + 1 : updatedRows.length;
    updatedRows.splice(insertIndex, 0, newRow);
    form.setFieldValue("details" as any, updatedRows as any);

    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["entity-select", "products"] });

    toast.success(`Đã nhân bản và thêm sản phẩm "${newProduct.productName || newProduct.name}" vào báo giá!`);
    setCloneModalOpen(false);
    setCloningInitialData(null);
    setCloningRowIndex(null);
  };

  /**
   * Thêm mốc số lượng bậc thang (Tiered Pricing - TSK-EST-03)
   */
  const handleAddQuantityTier = (
    sourceRow: QuotationDetailFormData,
    sourceIndex: number,
    targetQty: number | number[],
  ) => {
    const currentRows = (form.getFieldValue("details") as QuotationDetailFormData[]) || [];
    const quantities = Array.isArray(targetQty) ? targetQty : [targetQty];
    const basePrice = Number(sourceRow.quotedPrice || sourceRow.priceVND || 0);

    const newRows: QuotationDetailFormData[] = quantities
      .filter((qty) => qty !== sourceRow.quantity)
      .map((qty) => {
        // Chiết khấu bậc thang tham khảo theo quy mô bài in Offset
        const discountFactor =
          qty <= 5000 ? 1.0 : qty <= 10000 ? 0.95 : qty <= 20000 ? 0.90 : 0.85;
        const tierPrice = Math.round(basePrice * discountFactor);
        return {
          ...sourceRow,
          id: undefined, // Dòng mới
          quantity: qty,
          priceVND: tierPrice,
          quotedPrice: tierPrice,
          notes: `Mốc ${qty.toLocaleString()} cái (Bậc thang)`,
        };
      });

    if (newRows.length === 0) {
      toast.warning("Mốc số lượng này đã trùng với dòng hiện tại");
      return;
    }

    const updatedRows = [...currentRows];
    updatedRows.splice(sourceIndex + 1, 0, ...newRows);
    form.setFieldValue("details" as any, updatedRows as any);

    toast.success(
      `Đã thêm ${newRows.length} mốc số lượng bậc thang cho "${sourceRow.productName || 'sản phẩm'}"!`,
    );
  };

  const selectedCurrency = useStore(
    form.store,
    (state: any) => (state.values as QuotationFormData).currency || "VND",
  );

  const formatCurrency = (amount: number, currency = selectedCurrency) => {
    if (currency === "VND" || !currency) {
      return `${formatMoney(amount)}đ`;
    }
    return `${formatMoney(amount)} ${currency}`;
  };

  const isNonMoqValue = useStore(
    form.store,
    (state: any) => Boolean((state.values as QuotationFormData).isNonMoq),
  );

  const detailColumns = useMemo<DetailColumn<QuotationDetailFormData>[]>(() => {
    const cols: DetailColumn<QuotationDetailFormData>[] = [
      {
        key: "productName",
        label: "Sản phẩm / Quy cách",
        width: "min-w-[300px]",
        render: (_v, row, i, updateRow) => (
          <div className="flex flex-col gap-1 py-0.5">
            <SharedCombobox
              type="product"
              params={{ customerId: selectedCustomerId }}
              value={row.productId || ""}
              valueLabel={row.productName || (row.productCode ? `[${row.productCode}] ${row.productName}` : "")}
              breadcrumb={["Báo giá", "Chi tiết", "Sản phẩm"]}
              onChange={(id, selectedObj) => {
                if (id) {
                  const orig = (selectedObj as any)?.original || selectedObj || {};
                  const formattedSpecs = formatProductSpecs(orig.specs, orig);
                  const specsStr = formattedSpecs || [
                    orig.length && orig.width ? `${orig.length}x${orig.width}${orig.height ? `x${orig.height}` : ''}mm` : '',
                    orig.material?.name || orig.material?.materialId || orig.paperType || '',
                    orig.printColors || '',
                  ].filter(Boolean).join(' • ') || (typeof orig.packingSpec === 'string' ? orig.packingSpec : '') || "";

                  const resolvedPricing = resolveProductCatalogPricing(orig);
                  const catalogPrice = resolvedPricing.catalogPrice;
                  const nextQuotedPrice =
                    row.quotedPrice && row.quotedPrice > 0
                      ? row.quotedPrice
                      : catalogPrice;

                  updateRow({
                    productId: id,
                    productCode: orig.productCode || orig.code || "",
                    productName: orig.productName || (selectedObj as any)?.label || orig.name || "",
                    unit: orig.unit || row.unit || "Cái",
                    specs: specsStr,
                    process: orig.routingSummary || orig.process || row.process || "",
                    priceVND: catalogPrice,
                    quotedPrice: nextQuotedPrice,
                    amount: (row.quantity || 1) * nextQuotedPrice,
                    costPrice: resolvedPricing.costPrice,
                    profitMargin: resolvedPricing.profitMargin,
                    isCombo: !!orig.isComposite || !!orig.isCombo,
                    isCommercial: !!orig.isCommercial,
                  } as any);
                } else {
                  updateRow({
                    productId: "",
                    productCode: "",
                    productName: "",
                  });
                }
              }}
              placeholder={selectedCustomerId ? "Chọn sản phẩm..." : "Chọn KH trước"}
              disabled={!selectedCustomerId}
              hideLabel
            />
            {row.productId ? (
              <div className="flex items-start justify-between gap-1 px-1">
                <div className="flex flex-wrap items-center gap-1 text-[11px] text-slate-600">
                  {row.isCombo && <Badge variant="outline" className="text-[10px] py-0 px-1 border-purple-200 text-purple-700 bg-purple-50 h-4">COMBO</Badge>}
                  {row.isCommercial && <Badge variant="outline" className="text-[10px] py-0 px-1 border-amber-200 text-amber-700 bg-amber-50 h-4">THƯƠNG MẠI</Badge>}
                  {row.productCode && <span className="font-mono text-xs bg-slate-100 text-slate-900 px-1.5 py-px rounded font-bold">{row.productCode}</span>}
                  {row.specs ? (
                    <span className="italic text-slate-500 line-clamp-1">
                      {typeof row.specs === "string" ? row.specs : formatProductSpecs(row.specs, row)}
                    </span>
                  ) : (
                    <span className="text-slate-400 italic">Chưa có quy cách</span>
                  )}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Chỉnh sửa sản phẩm gốc (Canonical Form)..."
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingProductId(row.productId || null);
                      setEditingProductRowIndex(i);
                    }}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                    title="Nhân bản sản phẩm này làm SP mới (Canonical Form)..."
                    disabled={isPreparingClone}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartClone(row, i);
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ) : row.productName ? (
              <div className="px-1 text-[11px] text-amber-600 font-medium">
                ⚠️ Chưa liên kết SP hệ thống: {row.productName}
              </div>
            ) : null}
          </div>
        )
      },
      {
        key: "unit",
        label: "ĐVT",
        width: "w-24",
        align: "center",
        render: (_v, row, _i, updateRow) => (
          <Input
            value={row.unit || ""}
            onChange={(e) => updateRow({ unit: e.target.value })}
            className="h-8 text-xs text-center border-slate-200"
            placeholder="ĐVT"
          />
        )
      },
      {
        key: "quantity",
        label: isNonMoqValue ? (
          <span className="flex items-center justify-end gap-1">
            <span>SL</span>
            <span className="bg-purple-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded leading-none shadow-xs">
              NON-MOQ
            </span>
          </span>
        ) : (
          "SL"
        ),
        width: isNonMoqValue ? "w-28" : "w-24",
        align: "right",
        required: true,
        render: (_v, row, _i, updateRow) => (
          <div className="relative">
            <NumericInput
              value={row.quantity || null}
              decimals={0}
              placeholder={isNonMoqValue ? "SL mẫu" : "1"}
              onChange={(val) => {
                const q = val || 0;
                const unitP = row.quotedPrice || row.priceVND || 0;
                updateRow({ quantity: q, amount: q * unitP } as any);
              }}
              className={cn(
                "h-8 text-xs text-right font-medium",
                isNonMoqValue && "border-purple-300 focus-visible:ring-purple-400 bg-purple-50/20 text-purple-900"
              )}
            />
          </div>
        )
      },
      {
        key: "priceVND",
        label: "Đơn giá (Catalog)",
        width: "w-28",
        align: "right",
        render: (_v, row, _i, updateRow) => (
          <NumericInput
            data-testid={`quotation-row-catalog-price-${_i}`}
            value={row.priceVND || null}
            decimals={0}
            placeholder="0"
            title="Đơn giá tham chiếu từ danh mục / dự tính ban đầu"
            onChange={(val) => {
              const p = val || 0;
              updateRow({
                priceVND: p,
              });
            }}
            className="h-8 text-xs text-right text-slate-700"
          />
        )
      },
      {
        key: "quotedPrice",
        label: "Giá chốt (Tính tiền)",
        width: "w-32",
        align: "right",
        render: (_v, row, index, updateRow) => {
          const isInvalid = !row.quotedPrice || Number(row.quotedPrice) <= 0;
          return (
            <div className="space-y-0.5">
              <NumericInput
                data-field={`details.${index}.quotedPrice`}
                value={row.quotedPrice || null}
                decimals={0}
                placeholder="Nhập giá..."
                title="Đơn giá chốt thương thảo thực tế với khách — dùng để tính Thành tiền (Bắt buộc > 0)"
                onChange={(val) => {
                  const qp = val || 0;
                  updateRow({ quotedPrice: qp, amount: (row.quantity || 0) * qp } as any);
                }}
                className={cn(
                  "h-8 text-xs text-right font-semibold",
                  isInvalid
                    ? "border-destructive text-destructive bg-rose-50/50 ring-1 ring-destructive/40 focus:ring-2 focus:ring-destructive"
                    : "text-emerald-700 bg-emerald-50/30 border-emerald-300"
                )}
              />
              {isInvalid && (
                <span className="text-[10px] text-destructive block text-right font-medium leading-none">Bắt buộc</span>
              )}
            </div>
          );
        }
      },
      {
        key: "id" as any,
        label: "Thành tiền",
        width: "w-32",
        align: "right",
        render: (_v, row) => {
          const lineTotal = (row.quantity || 0) * (row.quotedPrice || 0);
          return (
            <div className="text-right font-mono font-semibold text-xs py-2 px-1 text-slate-800 whitespace-nowrap">
              {formatCurrency(lineTotal)}
            </div>
          );
        }
      },
      {
        key: "notes" as any,
        label: "Thao tác",
        width: "w-44",
        align: "center",
        render: (_val, row, i) => (
          <div className="flex items-center justify-center gap-1 py-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
              title="Chỉnh sửa chi tiết nâng cao..."
              onClick={(e) => {
                 e.stopPropagation();
                 setEditDetailIndex(i);
                 setIsDetailModalOpen(true);
              }}
            >
              Sửa
            </Button>
            {row.productId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs flex items-center gap-0.5"
                    title="Thêm mốc số lượng bậc thang (Tiered Pricing)..."
                  >
                    <Layers className="h-3.5 w-3.5 mr-0.5" /> + Mốc
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 text-xs">
                  <div className="px-2 py-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Mốc số lượng bậc thang
                  </div>
                  <DropdownMenuItem onClick={() => handleAddQuantityTier(row, i, 5000)}>
                    Mốc 5.000 cái (Chuẩn)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddQuantityTier(row, i, 10000)}>
                    Mốc 10.000 cái (Giảm ~5%)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddQuantityTier(row, i, 20000)}>
                    Mốc 20.000 cái (Giảm ~10%)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddQuantityTier(row, i, 50000)}>
                    Mốc 50.000 cái (Giảm ~15%)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="font-medium text-blue-600 cursor-pointer"
                    onClick={() => handleAddQuantityTier(row, i, [5000, 10000, 20000, 50000])}
                  >
                    ⚡ Thêm cả 4 mốc (5k, 10k, 20k, 50k)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {row.productId && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                title="Nhân bản sản phẩm này làm SP mới (Canonical Form)..."
                disabled={isPreparingClone}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartClone(row, i);
                }}
              >
                <Copy className="h-3.5 w-3.5 mr-0.5" /> Copy
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-7 px-2 text-slate-700 hover:bg-slate-50"
              onClick={(e) => {
                e.stopPropagation();
                if (!row.id || row.id.length < 5) {
                  toast.error(
                    "Vui lòng lưu báo giá (Lưu nháp hoặc Lưu) trước khi tính giá chi tiết.",
                  );
                  return;
                }
                setEstimatingRowId(row.id as string);
                setEstimatingRowIndex(i);
              }}
              disabled={row.isCombo || !!row.isCommercial}
              title={
                row.isCommercial
                  ? "Đơn hàng thương mại không cần tính giá nội bộ"
                  : "Tính giá bằng Pricing Engine"
              }
            >
              Tính giá
            </Button>
          </div>
        ),
      }
    ];

    return cols;
  }, [selectedCustomerId, selectedCurrency, isNonMoqValue]);

  /* ── TextField helper ── */
  const TextField = ({
    name,
    label,
    required,
    placeholder,
    type = "text",
    className,
  }: {
    name: keyof QuotationFormData;
    label: string;
    required?: boolean;
    placeholder?: string;
    type?: string;
    className?: string;
  }) => (
    <form.Field name={name as any}>
      {(field) => (
        <TanstackFieldWrapper
          field={field as any}
          label={label}
          required={required}
          className={className}
        >
          <TanstackFormControl>
            <Input
              id={field.name}
              type={type}
              value={(field.state.value as any) ?? ""}
              placeholder={placeholder}
              className="h-9 text-sm"
              onChange={(e) =>
                (field.handleChange as any)(
                  type === "number"
                    ? e.target.value
                      ? Number(e.target.value)
                      : undefined
                    : e.target.value,
                )
              }
              onBlur={field.handleBlur}
            />
          </TanstackFormControl>
        </TanstackFieldWrapper>
      )}
    </form.Field>
  );

  return (
    <>
      <form onSubmit={handleFormSubmit} className="space-y-3">
      {orderCount !== undefined && orderCount > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Cảnh báo: Báo giá đã được lên Đơn hàng!</AlertTitle>
          <AlertDescription>
            Báo giá này đã có {orderCount} đơn hàng liên kết. Bất kỳ thay đổi
            nào cũng có thể ảnh hưởng đến tính nhất quán của các đơn hàng hiện
            tại. Hệ thống sẽ tự động tăng version nếu bạn lưu các thay đổi mới.
          </AlertDescription>
        </Alert>
      )}

      {/* Trường ẩn */}
      <form.Field name="createdDate">
        {(field) => (
          <input type="hidden" value={(field.state.value as string) ?? ""} />
        )}
      </form.Field>

      {/* ═══ Flat card: Header + Terms ═══ */}
      <div className="border rounded-lg bg-white shadow-sm p-3 space-y-2">
        {/* ── Thông tin báo giá ── */}
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider">
            Thông tin chung
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="col-span-2 md:col-span-2">
            <form.Field name="customerId">
              {(field) => (
                <div className="space-y-1" id="customerId" data-field="customerId">
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    Khách hàng <span className="text-destructive font-bold">*</span>
                  </Label>
                  <SharedCombobox
                    id="customerId"
                    dataField="customerId"
                    required={true}
                    error={hasFieldError(field)}
                    type="customer"
                    value={(field.state.value as string) || ""}
                    valueLabel={form.getFieldValue("customerName") || defaultValues?.customerName || (defaultValues as any)?.customer?.shortName || (defaultValues as any)?.customer?.name}
                    breadcrumb={["Báo giá", "Khách hàng"]}
                    onChange={(id, opt) => {
                      field.handleChange(id);
                      setSelectedCustomerId(id);
                      if (opt) {
                        const original = opt.original || {};
                        form.setFieldValue(
                          "customerName",
                          original.name || original.shortName || "",
                        );
                        form.setFieldValue(
                          "contactPerson",
                          original.contactPerson || "",
                        );
                        form.setFieldValue(
                          "phone",
                          original.phone || original.mobile || "",
                        );
                        form.setFieldValue("email", original.email || "");
                        form.setFieldValue(
                          "deliveryAddress",
                          original.deliveryAddress || original.address || "",
                        );
                        if (original.paymentTerms || original.paymentTerm) {
                          form.setFieldValue(
                            "paymentTerm",
                            original.paymentTerms || original.paymentTerm,
                          );
                        }
                        if (original.deposit) {
                          form.setFieldValue("deposit", original.deposit);
                        }
                        if (original.deliveryTime) {
                          form.setFieldValue("deliveryTime", original.deliveryTime);
                        }
                      } else {
                        form.setFieldValue("customerName", "");
                      }
                    }}
                    hideLabel={true}
                    placeholder="Chọn khách hàng..."
                    quickAddLabel={
                      hasPermission("customers", "create")
                        ? "Thêm KH mới"
                        : undefined
                    }
                    canAdd={hasPermission("customers", "create")}
                  />
                  {selectedCustomerId && (
                    <div className="flex items-center justify-end mt-1 gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px] text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50 font-medium flex items-center gap-1 border border-indigo-200/60 rounded cursor-pointer"
                        onClick={() => setShowEditCustomerModal(true)}
                        title="Cập nhật nhanh hồ sơ pháp nhân, MST, liên hệ của khách hàng này"
                      >
                        <UserCog className="h-3 w-3" />
                        Cập nhật hồ sơ KH
                      </Button>
                    </div>
                  )}
                  {showEditCustomerModal && selectedCustomerId && (
                    <CustomerFormModal
                      open={showEditCustomerModal}
                      onClose={() => setShowEditCustomerModal(false)}
                      customerId={selectedCustomerId}
                      mode="edit"
                      breadcrumb={["Báo giá", "Khách hàng", "Sửa thông tin"]}
                      onSuccess={(updated) => {
                        const orig = updated.original || {};
                        form.setFieldValue("customerName", orig.name || orig.shortName || "");
                        if (orig.contactPerson) form.setFieldValue("contactPerson", orig.contactPerson);
                        if (orig.phone || orig.mobile) form.setFieldValue("phone", orig.phone || orig.mobile);
                        if (orig.email) form.setFieldValue("email", orig.email);
                        if (orig.deliveryAddress || orig.address) form.setFieldValue("deliveryAddress", orig.deliveryAddress || orig.address);
                        if (orig.paymentTerms || orig.paymentTerm) form.setFieldValue("paymentTerm", orig.paymentTerms || orig.paymentTerm);
                        if (orig.deposit) form.setFieldValue("deposit", orig.deposit);
                        if (orig.deliveryTime) form.setFieldValue("deliveryTime", orig.deliveryTime);
                        queryClient.invalidateQueries({ queryKey: ["customers"] });
                        queryClient.invalidateQueries({ queryKey: ["combobox", "customer"] });
                        toast.success("Đã đồng bộ thông tin khách hàng vào báo giá!");
                      }}
                    />
                  )}
                </div>
              )}
            </form.Field>
          </div>
          <form.Field name="creatorId">
            {(field) => (
              <div className="col-span-1 md:col-span-1">
                <SharedCombobox
                  type="user"
                  value={field.state.value || ""}
                  onChange={(id) => field.handleChange(id)}
                  label="NV Kinh doanh phụ trách"
                  placeholder="Chọn nhân viên..."
                />
              </div>
            )}
          </form.Field>
          <div className="col-span-1 md:col-span-1">
            <TextField
              name="validUntil"
              label="Hiệu lực đến"
              required
              type="date"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {defaultValues?.quotationId && (
            <form.Field name="quotationId">
              {(field) => (
                <div className="space-y-1 col-span-1 md:col-span-1">
                  <Label className="text-xs font-semibold text-slate-700">Mã báo giá</Label>
                  <Input
                    value={(field.state.value as string) ?? ""}
                    readOnly
                    className="font-mono bg-white text-emerald-700 font-semibold h-9 text-sm"
                  />
                </div>
              )}
            </form.Field>
          )}
          <form.Subscribe
            selector={(s) => ({
              currency: (s.values as QuotationFormData).currency,
              exchangeRate: (s.values as QuotationFormData).exchangeRate,
            })}
          >
            {({ currency, exchangeRate }) => (
              <div className="col-span-1 md:col-span-1">
                <CurrencyExchangeField
                  currency={currency || "VND"}
                  exchangeRate={exchangeRate || 1}
                  onCurrencyChange={(v, rate) => {
                    form.setFieldValue("currency", v as any);
                    form.setFieldValue("exchangeRate", rate as any);
                  }}
                  onExchangeRateChange={(v) =>
                    form.setFieldValue("exchangeRate", v as any)
                  }
                  hideRateInput={true}
                />
              </div>
            )}
          </form.Subscribe>
          <form.Field name="isNonMoq">
            {(field) => (
              <div className="col-span-1 md:col-span-1 space-y-1">
                <Label className="text-xs font-semibold text-slate-700">Đặc thù đơn hàng</Label>
                <div className={cn(
                  "flex items-center justify-between h-9 px-2.5 rounded-md border text-xs transition-colors",
                  field.state.value
                    ? "bg-purple-50 border-purple-300 text-purple-900 font-semibold"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                )}>
                  <div className="flex items-center gap-1.5 truncate">
                    <span className="text-xs font-medium">NON-MOQ</span>
                    {field.state.value && (
                      <Badge className="text-[9px] px-1 py-0 bg-purple-600 text-white border-none font-bold">
                        Chạy mẫu
                      </Badge>
                    )}
                  </div>
                  <Switch
                    checked={Boolean(field.state.value)}
                    onCheckedChange={(checked) => field.handleChange(checked)}
                    className="data-[state=checked]:bg-purple-600 scale-90"
                  />
                </div>
              </div>
            )}
          </form.Field>
        </div>
      </div>

      {/* ═══ Điều khoản thương mại & Giao hàng ═══ */}
      <div className="border rounded-lg bg-white shadow-sm p-3 space-y-2">
        <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-2">
          <Truck className="h-4 w-4 text-emerald-600" /> Điều khoản thương mại & Giao hàng
        </h3>

        {/* 4 trường cùng hàng: Đặt cọc, Điều khoản, Thời hạn, Dung sai */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <form.Field name="deposit">
            {(field) => (
              <div className="col-span-1 space-y-1">
                <ComboboxConfigMaster
                  categoryCode="DEPOSIT_OPTIONS"
                  value={(field.state.value as string) || ""}
                  onChange={(v) => field.handleChange(v as any)}
                  label="Đặt cọc"
                  placeholder="VD: 30%"
                  error={!!field.state.meta.errors.length}
                />
              </div>
            )}
          </form.Field>
          <form.Field name="paymentTerm">
            {(field) => (
              <div className="col-span-1 space-y-1">
                <ComboboxConfigMaster
                  categoryCode="PAYMENT_TERMS"
                  value={(field.state.value as string) || ""}
                  onChange={(v) => field.handleChange(v as any)}
                  label="Điều khoản TT"
                  placeholder="VD: Net 30"
                  error={!!field.state.meta.errors.length}
                />
              </div>
            )}
          </form.Field>
          <form.Field name="deliveryTime">
            {(field) => (
              <div className="col-span-1 space-y-1">
                <ComboboxConfigMaster
                  categoryCode="DELIVERY_TIME"
                  value={(field.state.value as string) || ""}
                  onChange={(v) => field.handleChange(v as any)}
                  label="TG giao hàng"
                  placeholder="VD: 15 ngày"
                  error={!!field.state.meta.errors.length}
                />
              </div>
            )}
          </form.Field>
          <form.Field name="quantityTolerance">
            {(field) => (
              <div className="col-span-1 space-y-1">
                <EditableCombobox
                  suggestions={["+/- 10%", "+/- 5%", "Không bù hao (0%)"]}
                  value={(field.state.value as string) || ""}
                  onChange={(v) => field.handleChange(v as any)}
                  label="Dung sai SL (Bù hao)"
                  placeholder="Gõ hoặc chọn..."
                  error={!!field.state.meta.errors.length}
                />
              </div>
            )}
          </form.Field>
        </div>

        <div className="pt-2 pb-0.5">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            Thông tin người liên hệ & Giao nhận
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
          <TextField
            name="contactPerson"
            label="Người liên hệ"
            placeholder="Tên người nhận hàng"
            className="col-span-1 md:col-span-1"
          />
          <TextField
            name="phone"
            label="Điện thoại"
            placeholder="Số điện thoại"
            className="col-span-1 md:col-span-1"
          />
          <TextField
            name="email"
            label="Email"
            type="email"
            placeholder="email@company.vn"
            className="col-span-2 md:col-span-2"
          />
          <div className="col-span-2 md:col-span-4 flex items-end gap-2">
            <TextField
              name="deliveryAddress"
              label="Địa chỉ giao hàng"
              placeholder="Nhập địa chỉ nhà xưởng / kho giao nhận..."
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!selectedCustomerId}
              className="h-9 px-3 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800 border-emerald-300 font-medium flex items-center gap-1.5 cursor-pointer whitespace-nowrap mb-0.5 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                selectedCustomerId
                  ? "Cập nhật nhanh Người liên hệ, SĐT, Email và Địa chỉ này vào hồ sơ Khách hàng"
                  : "Vui lòng chọn khách hàng trước khi lưu cập nhật"
              }
              onClick={async () => {
                if (!selectedCustomerId) {
                  toast.error("Vui lòng chọn khách hàng trước khi lưu cập nhật");
                  return;
                }
                const contactPerson = form.getFieldValue("contactPerson");
                const phone = form.getFieldValue("phone");
                const email = form.getFieldValue("email");
                const deliveryAddress = form.getFieldValue("deliveryAddress");
                if (!contactPerson && !phone && !email && !deliveryAddress) {
                  toast.error("Vui lòng nhập ít nhất Người liên hệ, SĐT, Email hoặc Địa chỉ để lưu");
                  return;
                }
                try {
                  await trpcClient.customers.update.mutate({
                    id: selectedCustomerId,
                    data: {
                      contactPerson: contactPerson || undefined,
                      phone: phone || undefined,
                      email: email || undefined,
                      address: deliveryAddress || undefined,
                    },
                  });
                  queryClient.invalidateQueries({ queryKey: ["customers"] });
                  queryClient.invalidateQueries({ queryKey: ["combobox", "customer"] });
                  toast.success("Đã lưu thông tin khách hàng thành công!");
                } catch (err: any) {
                  toast.error(err?.message || "Cập nhật thông tin khách hàng thất bại");
                }
              }}
            >
              <Save className="h-3.5 w-3.5" />
              Lưu thông tin khách hàng
            </Button>
          </div>
        </div>
      </div>

      {/* ═══ Chi tiết sản phẩm — giữ border riêng ═══ */}
      <div className="border rounded-lg bg-white shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
              Chi tiết sản phẩm <span className="text-destructive font-bold">*</span>
            </h3>
            <form.Field name="isNonMoq">
              {(field) => (
                <button
                  type="button"
                  onClick={() => field.handleChange(!field.state.value)}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all border shadow-2xs cursor-pointer",
                    field.state.value
                      ? "bg-purple-600 text-white border-purple-700 hover:bg-purple-700"
                      : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                  )}
                  title="Bật/tắt chế độ báo giá không áp MOQ (Đơn chạy mẫu, chạy tay)"
                >
                  <span className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    field.state.value ? "bg-white animate-pulse" : "bg-slate-400"
                  )} />
                  <span>NON-MOQ</span>
                  <span className={cn(
                    "text-[10px] px-1 rounded",
                    field.state.value ? "bg-white/20 text-white" : "text-slate-400"
                  )}>
                    {field.state.value ? "Bật" : "Tắt"}
                  </span>
                </button>
              )}
            </form.Field>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() =>
                downloadExcelTemplate(
                  DETAIL_EXPORT_COLUMNS,
                  "chi_tiet_bao_gia",
                  QUOTATION_SAMPLE_DATA,
                )
              }
            >
              <FileDown className="h-3.5 w-3.5" /> Tải mẫu Excel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" />
              {importing ? "Đang import..." : "Import Excel"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleImportDetails}
            />
          </div>
        </div>
        <div className="px-5 pb-4">
          <form.Field name="details">
            {(field) => {
              const hasErr = field.state.meta.errors.length > 0;
              return (
                <div
                  id="details"
                  data-field="details"
                  className={cn(
                    "transition-all duration-200",
                    hasErr && "rounded-lg border-2 border-destructive p-1 ring-2 ring-destructive/30"
                  )}
                >
                  <InlineDetailTable<QuotationDetailFormData>
                  columns={detailColumns}
                  rows={(field.state.value as QuotationDetailFormData[]) || []}
                  createEmptyRow={() => ({ ...EMPTY_DETAIL })}
                  onAddRequest={() => {
                    const currentRows = (field.state.value as QuotationDetailFormData[]) || [];
                    field.handleChange([...currentRows, { ...EMPTY_DETAIL }]);
                  }}
                  onChange={(newRows) => {
                    field.handleChange(newRows);
                  }}
                  footer={(rows) => {
                    const totalQty = rows.reduce((s, d) => s + Number(d.quantity || 0), 0);
                    const subtotal = rows.reduce(
                      (s, d) => s + Number(d.quantity || 0) * Number(d.quotedPrice || 0),
                      0
                    );
                    const vatRate = Number(form.getFieldValue("vatRate") ?? 8);
                    const vatAmount = (subtotal * vatRate) / 100;
                    const grandTotal = subtotal + vatAmount;

                    return (
                      <>
                        <tr className="border-t border-slate-200">
                          <td colSpan={2} className="px-3 py-2 text-slate-600 font-semibold text-xs">
                            Tổng cộng ({rows.length} sản phẩm)
                          </td>
                          <td className="px-2 py-2 text-right font-mono font-bold text-xs text-slate-800">
                            {formatNumber(totalQty)}
                          </td>
                          <td colSpan={2} className="px-2 py-2 text-right text-xs text-slate-500 font-medium">
                            Tạm tính:
                          </td>
                          <td className="px-2 py-2 text-right font-mono font-bold text-xs text-slate-900 whitespace-nowrap">
                            {formatCurrency(subtotal)}
                          </td>
                          <td></td>
                        </tr>
                        <tr className="border-t border-slate-100 bg-emerald-50/40">
                          <td colSpan={5} className="px-3 py-1.5 text-right text-xs text-emerald-800 font-medium">
                            VAT ({vatRate}%): <span className="font-mono whitespace-nowrap">{formatCurrency(vatAmount)}</span> | 
                            <span className="font-bold ml-2">Tổng thanh toán:</span>
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-sm text-emerald-700 whitespace-nowrap">
                            {formatCurrency(grandTotal)}
                          </td>
                          <td></td>
                        </tr>
                      </>
                    );
                  }}
                />
                {field.state.meta.errors.length > 0 && (
                  <div className="text-[0.8rem] font-medium text-destructive mt-1">
                    {field.state.meta.errors.map((e: any) => typeof e === 'object' && e ? e.message || JSON.stringify(e) : String(e)).join(", ")}
                  </div>
                )}
              </div>
            );
          }}
        </form.Field>
        </div>
      </div>

      {/* ═══ Ghi chú & Comments ═══ */}
      <div className="border rounded-lg bg-white shadow-sm p-3 space-y-2">
        <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-2">
          <Info className="h-4 w-4 text-emerald-600" /> Ghi chú & Comments
        </h3>
        <form.Field name="notes">
          {(field) => (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">
                  Ghi chú (chọn nhanh)
                </Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Báo giá dự kiến",
                    "Cần gửi mẫu đối",
                    "Xác nhận lại với xưởng",
                    "Khách hàng quan trọng",
                  ].map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 text-xs font-normal text-slate-600 py-1 px-2"
                      onClick={() => {
                        const current = field.state.value || "";
                        const appendStr = current
                          ? current.trim().endsWith(";")
                            ? ` ${tag}`
                            : `; ${tag}`
                          : tag;
                        field.handleChange(current + appendStr);
                      }}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor={field.name}
                  className="text-xs font-semibold text-slate-600"
                >
                  Ghi chú (in trên báo giá gửi khách)
                </Label>
                <Textarea
                  id={field.name}
                  value={(field.state.value as string) ?? ""}
                  rows={3}
                  placeholder="Nhập ghi chú nội bộ cho báo giá này..."
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            </div>
          )}
        </form.Field>
      </div>

      {/* ═══ Actions ═══ */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 z-50 flex items-center justify-end gap-3 -mx-4 -mb-4 mt-6 shadow-[0_-10px_15px_-3px_rgb(0,0,0,0.05)] rounded-b-lg">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="whitespace-nowrap h-9"
        >
          Huỷ
        </Button>
        <form.Subscribe selector={(s) => [s.isSubmitting]}>
          {([submitting]) => (
            <SaveActionButtons
              status={currentStatus}
              busy={Boolean(submitting || isSubmitting || isDraftSaving)}
              onSaveDraft={handleSaveDraft}
              submitLabel={submitLabel}
            />
          )}
        </form.Subscribe>
      </div>

      {/* Wizard Modal */}
      {estimatingRowId && (
        <QuotationEstimatingModal
          isOpen={!!estimatingRowId}
          onClose={() => {
            setEstimatingRowId(null);
            setEstimatingRowIndex(null);
          }}
          quotationDetailId={estimatingRowId}
          orderQty={
            estimatingRowIndex !== null
              ? form.state.values.details[estimatingRowIndex]?.quantity
              : 1
          }
          productName={
            estimatingRowIndex !== null
              ? form.state.values.details[estimatingRowIndex]?.productName
              : ''
          }
          specs={
            estimatingRowIndex !== null
              ? form.state.values.details[estimatingRowIndex]?.specs
              : ''
          }
          onSuccess={() => {
            if (estimatingRowIndex !== null) {
              toast.success("Đã cập nhật dự toán kỹ thuật thành công!");
            }
          }}
        />
      )}

      {/* Chi tiết Modal */}
      {isDetailModalOpen && (
        <form.Field name="details">
          {(field) => {
            const rows = (field.state.value as QuotationDetailFormData[]) || [];
            const initialData =
              editDetailIndex !== null ? rows[editDetailIndex] : null;
            return (
              <QuotationDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => {
                  setIsDetailModalOpen(false);
                  setEditDetailIndex(null);
                }}
                currency={form.state.values.currency || "VND"}
                customerId={form.state.values.customerId}
                initialData={initialData}
                onSave={(data) => {
                  if (editDetailIndex !== null) {
                    const newRows = [...rows];
                    newRows[editDetailIndex] = data;
                    field.handleChange(newRows);
                  } else {
                    field.handleChange([...rows, data]);
                  }
                }}
              />
            );
          }}
        </form.Field>
      )}

      </form>

      {/* Canonical ProductFormModal for deep cloning */}
      {cloneModalOpen && (
        <ProductFormModal
          open={cloneModalOpen}
          initialData={cloningInitialData || undefined}
          breadcrumb={["Báo giá", "Chi tiết", "Nhân bản sản phẩm"]}
          onClose={() => {
            setCloneModalOpen(false);
            setCloningInitialData(null);
            setCloningRowIndex(null);
          }}
          onSuccess={handleCloneProductSuccess}
        />
      )}

      {/* Canonical ProductEditModal for editing product in-place */}
      {editingProductId && (
        <ProductEditModal
          productId={editingProductId}
          isOpen={true}
          breadcrumb={["Báo giá", "Sản phẩm", "Chỉnh sửa sản phẩm"]}
          onClose={() => {
            setEditingProductId(null);
            setEditingProductRowIndex(null);
          }}
          onSuccess={handleEditProductSuccess}
        />
      )}
    </>
  );
}
